<template>
    <div class="page-container">
        <div class="flex items-center gap-4 mb-8">
            <div class="bg-orange-600 p-3 rounded-2xl shadow-lg">
                <LucideIcon :size="24" class="text-white">
                    <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </LucideIcon>
            </div>
            <div>
                <h2 class="page-title text-2xl font-bold text-slate-900">{{ $t('household.expiry.title') }}</h2>
                <p class="section-subtitle text-slate-500">{{ $t('household.expiry.subtitle') }}</p>
            </div>
        </div>

        <!-- Статистика -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div class="stat-card">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.expiry.statExpired') }}</p>
                        <p class="text-4xl font-extrabold text-red-600">{{ expired.length }}</p>
                    </div>
                    <div class="bg-red-50 p-3 rounded-xl">
                        <LucideIcon :size="24" class="text-red-600">
                             <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>
                        </LucideIcon>
                    </div>
                </div>
            </div>

            <div class="stat-card">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.expiry.statSoon') }}</p>
                        <p class="text-4xl font-extrabold text-orange-600">{{ expiringSoon.length }}</p>
                    </div>
                    <div class="bg-orange-50 p-3 rounded-xl">
                        <LucideIcon :size="24" class="text-orange-600">
                            <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                        </LucideIcon>
                    </div>
                </div>
            </div>

            <div class="stat-card">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.expiry.statNormal') }}</p>
                        <p class="text-4xl font-extrabold text-green-600">{{ normal.length }}</p>
                    </div>
                    <div class="bg-green-50 p-3 rounded-xl">
                        <LucideIcon :size="24" class="text-green-600">
                            <path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>
                        </LucideIcon>
                    </div>
                </div>
            </div>
        </div>

        <!-- Списки -->
        <div v-if="loading" class="flex justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
        <div v-else class="space-y-8">
            <!-- Просроченные -->
            <div v-if="expired.length > 0" class="statement-card overflow-hidden">
                <div class="bg-red-500 px-8 py-4">
                    <h3 class="font-extrabold text-white text-lg">{{ $t('household.expiry.sectionExpired') }} ({{ expired.length }})</h3>
                </div>
                <div class="divide-y divide-slate-100 p-6">
                    <div v-for="item in expired" :key="item.id" class="py-4 first:pt-0 last:pb-0">
                        <div class="flex items-start justify-between">
                            <div class="flex-1">
                                <h4 class="font-black text-slate-900 text-base mb-2">{{ getNomenclatureName(item.nomenclatureId) }}</h4>
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-xs font-bold text-slate-500 uppercase tracking-tight">
                                    <p>{{ $t('household.expiry.warehousePrefix') }} <span class="text-slate-900">{{ getWarehouseName(item.warehouseId) }}</span></p>
                                    <p>{{ $t('household.expiry.addressPrefix') }} <span class="text-slate-900">{{ item.location.zone }}-{{ item.location.row }}-{{ item.location.rack }}-{{ item.location.level }}</span></p>
                                    <p>{{ $t('household.expiry.quantityPrefix') }} <span class="text-slate-900">{{ item.quantity }} {{ getNomenclatureUnit(item.nomenclatureId) }}</span></p>
                                    <p v-if="item.batchNumber">{{ $t('household.expiry.batchPrefix') }} <span class="text-slate-900">{{ item.batchNumber }}</span></p>
                                </div>
                            </div>
                            <div class="text-right">
                                <p class="text-lg font-black text-slate-900 mb-1">
                                    {{ formatDate(item.expiryDate) }}
                                </p>
                                <p class="text-xs font-black text-red-600 uppercase">
                                    {{ $t('household.expiry.expiredDays', { days: Math.abs(getDaysUntilExpiry(item.expiryDate)) }) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Истекают скоро -->
            <div v-if="expiringSoon.length > 0" class="statement-card overflow-hidden">
                <div class="bg-orange-500 px-8 py-4">
                    <h3 class="font-extrabold text-white text-lg">{{ $t('household.expiry.sectionSoon') }} ({{ expiringSoon.length }})</h3>
                </div>
                <div class="divide-y divide-slate-100 p-6">
                    <div v-for="item in expiringSoon" :key="item.id" class="py-4 first:pt-0 last:pb-0">
                        <div class="flex items-start justify-between">
                            <div class="flex-1">
                                <h4 class="font-black text-slate-900 text-base mb-2">{{ getNomenclatureName(item.nomenclatureId) }}</h4>
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-xs font-bold text-slate-500 uppercase tracking-tight">
                                    <p>{{ $t('household.expiry.warehousePrefix') }} <span class="text-slate-900">{{ getWarehouseName(item.warehouseId) }}</span></p>
                                    <p>{{ $t('household.expiry.addressPrefix') }} <span class="text-slate-900">{{ item.location.zone }}-{{ item.location.row }}-{{ item.location.rack }}-{{ item.location.level }}</span></p>
                                    <p>{{ $t('household.expiry.quantityPrefix') }} <span class="text-slate-900">{{ item.quantity }} {{ getNomenclatureUnit(item.nomenclatureId) }}</span></p>
                                    <p v-if="item.batchNumber">{{ $t('household.expiry.batchPrefix') }} <span class="text-slate-900">{{ item.batchNumber }}</span></p>
                                </div>
                            </div>
                            <div class="text-right">
                                <p class="text-lg font-black text-slate-900 mb-1">
                                    {{ formatDate(item.expiryDate) }}
                                </p>
                                <p class="text-xs font-black text-orange-600 uppercase">
                                    {{ $t('household.expiry.daysLeft', { days: getDaysUntilExpiry(item.expiryDate) }) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- В норме -->
            <div v-if="normal.length > 0" class="statement-card overflow-hidden">
                <div class="bg-green-500 px-8 py-4">
                    <h3 class="font-extrabold text-white text-lg">{{ $t('household.expiry.sectionNormal') }} ({{ normal.length }})</h3>
                </div>
                <div class="divide-y divide-slate-100 p-6">
                    <div v-for="item in normal" :key="item.id" class="py-4 first:pt-0 last:pb-0">
                        <div class="flex items-start justify-between">
                            <div class="flex-1">
                                <h4 class="font-black text-slate-900 text-base mb-2">{{ getNomenclatureName(item.nomenclatureId) }}</h4>
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-xs font-bold text-slate-500 uppercase tracking-tight">
                                    <p>{{ $t('household.expiry.warehousePrefix') }} <span class="text-slate-900">{{ getWarehouseName(item.warehouseId) }}</span></p>
                                    <p>{{ $t('household.expiry.addressPrefix') }} <span class="text-slate-900">{{ item.location.zone }}-{{ item.location.row }}-{{ item.location.rack }}-{{ item.location.level }}</span></p>
                                    <p>{{ $t('household.expiry.quantityPrefix') }} <span class="text-slate-900">{{ item.quantity }} {{ getNomenclatureUnit(item.nomenclatureId) }}</span></p>
                                    <p v-if="item.batchNumber">{{ $t('household.expiry.batchPrefix') }} <span class="text-slate-900">{{ item.batchNumber }}</span></p>
                                </div>
                            </div>
                            <div class="text-right">
                                <p class="text-lg font-black text-slate-900 mb-1">
                                    {{ formatDate(item.expiryDate) }}
                                </p>
                                <p class="text-[10px] text-slate-400 font-bold uppercase tracking-widest text-right">
                                    {{ $t('household.expiry.daysLeft', { days: getDaysUntilExpiry(item.expiryDate) }) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="!loading && expired.length === 0 && expiringSoon.length === 0 && normal.length === 0" class="empty-state-card mt-8">
                <LucideIcon :size="48" class="empty-icon">
                    <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </LucideIcon>
                <p>{{ $t('household.expiry.empty') }}</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LucideIcon from '@/components/common/LucideIcon.vue';
import { getExpiryDates } from '@/api/household_special';
import { getNomenclatures } from '@/api/household_nomenclatures';
import { getWarehouses } from '@/api/household_warehouses';

const { t } = useI18n();

const loading = ref(true);
const stockItems = ref([]);
const nomenclatures = ref([]);
const warehouses = ref([]);

const now = new Date();

const expired = computed(() => {
    return stockItems.value.filter(item => {
        const days = getDaysUntilExpiry(item.expiryDate);
        return days < 0;
    });
});

const expiringSoon = computed(() => {
    return stockItems.value.filter(item => {
        const days = getDaysUntilExpiry(item.expiryDate);
        return days >= 0 && days <= 30;
    });
});

const normal = computed(() => {
    return stockItems.value.filter(item => {
        const days = getDaysUntilExpiry(item.expiryDate);
        return days > 30;
    });
});

const fetchData = async () => {
    loading.value = true;
    try {
        const [expiryRes, nomenclaturesRes, warehousesRes] = await Promise.all([
            getExpiryDates(),
            getNomenclatures(),
            getWarehouses()
        ]);
        stockItems.value = Array.isArray(expiryRes.data) ? expiryRes.data : (expiryRes.data?.results || []);
        nomenclatures.value = Array.isArray(nomenclaturesRes.data) ? nomenclaturesRes.data : (nomenclaturesRes.data?.results || []);
        warehouses.value = Array.isArray(warehousesRes.data) ? warehousesRes.data : (warehousesRes.data?.results || []);
    } catch (error) {
        console.error('Error fetching expiry data:', error);
    } finally {
        loading.value = false;
    }
};

const getDaysUntilExpiry = (dateString) => {
    if (!dateString) return 0;
    const expiryDate = new Date(dateString);
    return Math.floor((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ru-RU');
};

const getNomenclatureName = (id) => {
    return nomenclatures.value.find(n => n.id === id)?.name || t('household.shared.unknown');
};

const getNomenclatureUnit = (id) => {
    return nomenclatures.value.find(n => n.id === id)?.unit || '';
};

const getWarehouseName = (id) => {
    return warehouses.value.find(w => w.id === id)?.name || t('household.shared.unknown');
};

onMounted(fetchData);
</script>

<style scoped>
.page-container { padding: 2rem; background: #f3f3f5; min-height: 100%; }
.page-title { color: #000000; font-size: 1.75rem; font-weight: 700; margin: 0; }
.section-subtitle { color: #64748b; font-size: 0.875rem; margin-top: 0.25rem; }

.stat-card {
    background: white;
    padding: 1.5rem 2rem;
    border-radius: 1.25rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01);
}

.statement-card {
    background: white;
    border-radius: 1.5rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01);
}

@keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }

.empty-state-card { text-align: center; padding: 5rem 2rem; background: white; border: 1px dashed #e2e8f0; border-radius: 1.25rem; }
.empty-icon { color: #cbd5e1; margin-bottom: 1.5rem; }
.empty-state-card p { color: #64748b; font-size: 1.1rem; margin-bottom: 0; }
</style>
